import type { ActionResult, AdminUser, AskAnswer, DigestPreview, DigestSendResult, ExtractRequest, ExtractResponse, MessageDraftsRequest, MessageDraftsResponse, ProfileUpdate, Role, SyncResponse, TelegramLinkCode, User } from '@crmsy/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Server said the request may succeed later (503 / 429): keep the action queued (§5.4). */
    public readonly retry = false,
  ) {
    super(message);
  }
}

/** Every call carries the CSRF header (§10) and surfaces 401 as `ApiError(401)`. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { 'X-Requested-With': 'crm', 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let message = res.statusText;
    let retry = res.status === 503 || res.status === 429 || res.status === 502 || res.status === 504;
    try {
      const body = (await res.json()) as { error?: string; retry?: boolean };
      if (body.error) message = body.error;
      if (body.retry !== undefined) retry = body.retry;
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(res.status, message, retry);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const fetchMe = () => api<User>('/me');
export const fetchSync = (since: number) => api<SyncResponse>(`/sync?since=${since}`);
export interface AuthConfig {
  devLogin: boolean;
  mock: boolean;
  /** Gemini drafting available (§9). */
  ai: boolean;
  /** Gemini extraction available (§9). */
  extract: boolean;
  /** Ask the CRM available (§20). */
  ask: boolean;
  /** Bot handle when Telegram is configured (§19), else null. */
  telegramBot: string | null;
}
export const fetchAuthConfig = () => api<AuthConfig>('/auth/config');
export const fetchDrafts = (body: MessageDraftsRequest) => api<MessageDraftsResponse>('/ai/message', { method: 'POST', body: JSON.stringify(body) });
export const askCrm = (question: string) => api<AskAnswer>('/ai/ask', { method: 'POST', body: JSON.stringify({ question }) });
export const fetchAskHistory = () => api<AskAnswer[]>('/ai/ask/history');
export const clearAskHistory = () => api<void>('/ai/ask/history', { method: 'DELETE' });
export const fetchExtract = (body: ExtractRequest) => api<ExtractResponse>('/ai/extract', { method: 'POST', body: JSON.stringify(body) });
export const patchMe = (body: ProfileUpdate) => api<User>('/me', { method: 'PATCH', body: JSON.stringify(body) });
export const telegramLinkCode = () => api<TelegramLinkCode>('/me/telegram/link', { method: 'POST' });
export const telegramUnlink = () => api<User>('/me/telegram', { method: 'DELETE' });
export const fetchDigestPreview = () => api<DigestPreview>('/digest/preview');
export const sendDigestNow = () => api<DigestSendResult>('/digest/send', { method: 'POST' });
export const adminUsers = () => api<AdminUser[]>('/admin/users');
export const adminInvite = (body: { email: string; role: Role; notionUserId: string | null }) => api<AdminUser>('/admin/users', { method: 'POST', body: JSON.stringify(body) });
export const adminUpdateUser = (id: string, body: { role?: Role; notionUserId?: string | null }) => api<AdminUser>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const adminRemoveUser = (id: string) => api<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' });
export const adminSendDigest = (id: string) => api<DigestSendResult>(`/admin/users/${id}/digest`, { method: 'POST' });
export const adminDigestPreview = (id: string) => api<DigestPreview>(`/admin/users/${id}/digest/preview`);
export const logout = () => api<{ ok: boolean }>('/auth/logout', { method: 'POST' });
export const postAction = (body: unknown) => api<ActionResult>('/actions', { method: 'POST', body: JSON.stringify(body) });
